/**
 * Module 87 — puzzles prepared ahead of the player, per track.
 *
 * While the player is on puzzle N, puzzles up to **N + LOOKAHEAD_DISTANCE** are
 * built and their routes searched, one board per idle moment. In steady state
 * that is ONE new board per puzzle, built while the win popup is up, so the
 * first endless board of a track (the first one whose route is not in the
 * shipped table) is ready several puzzles before the player reaches it.
 *
 * ## Why the walk order is the whole design
 *
 * The track generator is not a pure function of the ordinal: it carries an
 * anti-repetition window, and any call that is not `last + 1` resets it
 * (`TrackLevelGenerator.generate`). So a board built ahead is the board the
 * player would have got ONLY if every board is built in ordinal order, through
 * the same `_buildOrdinal` path play uses (which may skip the generator for a
 * scripted teaching board, exactly as play does). `lastBuilt` is that walk's
 * position; `noteBuilt` is told about every build, queued or inline, and a
 * build that is not `lastBuilt + 1` throws the queue away because everything in
 * it was built against a history that just changed.
 *
 * ## Idle windows, most idle first (user, 2026-10-03)
 *
 * `win` (the popup or the confetti after the slide-out) may fill the queue to
 * the full distance. `study` (a board nobody has moved on yet, or the home
 * screen) and `gap` (no pointer down for `LOOKAHEAD_GAP_MS`) fill it only to one
 * short of that, so in steady state the work lands in the win window and the
 * other two only catch up after an entry, a resume or a reload. `busy` (a drag,
 * a slide, the celebration's slide-out, an ad, the Daily) does nothing.
 */
export const LOOKAHEAD_DISTANCE = 5;
export const LOOKAHEAD_GAP_MS = 800;
/** The highest ordinal that should be built by now, given the window. */
export function lookaheadTarget(window, current, distance = LOOKAHEAD_DISTANCE) {
    if (window === 'win')
        return current + distance;
    if (window === 'study' || window === 'gap')
        return current + distance - 1;
    return current;
}
export class TrackLookahead {
    constructor(generator) {
        this.generator = generator;
        /** The last ordinal handed to the build path for this track (0 = none yet). */
        this._lastBuilt = 0;
        this._queue = new Map();
        /** The board the player is on in this track, as adopted — re-adopted on a switch back. */
        this.current = null;
    }
    get lastBuilt() {
        return this._lastBuilt;
    }
    /**
     * The next ordinal to build when the player is on `current`: the walk's next
     * step, but never the current puzzle or one already passed (a board adopted
     * from outside the walk — a fixture — leaves the walk behind; building from
     * `current + 1` is then a jump, exactly what an inline Next would do).
     */
    nextFor(current) {
        return Math.max(this._lastBuilt + 1, current + 1);
    }
    /** Ordinals waiting, in order (test and diagnostics seam). */
    get queued() {
        return [...this._queue.keys()].sort((a, b) => a - b);
    }
    has(ordinal) {
        return this._queue.has(ordinal);
    }
    /**
     * Every build of this track's boards reports here, queued or not. A build
     * that does not continue the walk invalidates everything queued.
     */
    noteBuilt(ordinal) {
        if (ordinal !== this._lastBuilt + 1)
            this._queue.clear();
        this._lastBuilt = ordinal;
    }
    put(ordinal, level) {
        this._queue.set(ordinal, level);
        if (level instanceof Promise) {
            // Swap the resolved board in so a later take hands back a ready one. An
            // entry replaced or taken meanwhile is left alone.
            void level.then((ready) => {
                if (this._queue.get(ordinal) === level)
                    this._queue.set(ordinal, ready);
            }, () => { });
        }
    }
    /** The prepared board for `ordinal`, consuming it and anything before it. */
    take(ordinal) {
        const hit = this._queue.get(ordinal) ?? null;
        for (const k of [...this._queue.keys()])
            if (k <= ordinal)
                this._queue.delete(k);
        return hit;
    }
    /** A dev/QA jump: nothing queued describes the future any more. */
    clear() {
        this._queue.clear();
    }
}
